import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { CompetitionClassRegistrationsCollapsibleDatatable } from '../components/datatables/competitionClassRegistrationsCollapsibleDatatable';
import { CompetitionSportClassesDatatable } from '../components/datatables/competitionSportClassesDatatable';
import { CupResultsDatatable } from '../components/datatables/cupResultsDatatable';
import DeleteCupDialog from '../components/dialogs/deleteCupDialog';
import RegenerateCupResultsDialog from '../components/dialogs/regenerateCupResultsDialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from '../components/ui/button';
import {
    Dialog,
} from "../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';

const CupSingle = () => {
    const { cupId } = useParams();
    const { auth } = useContext(AuthContext);

    const navigate = useNavigate();

    const [cup, setCup] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [participantsIsLoading, setParticipantsIsLoading] = useState(true);
    const [participatingBunnies, setParticipatingBunnies] = useState([]);
    const [bunniesLoading, setBunniesLoading] = useState(true);


    const fetchCup = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/cups/${cupId}`);
            setCup(response.data);
            setError(null);

            if (response.data.deleted_at) setError('Du försöker visa en raderad cup.');
        } catch (error) {
            if (error.response?.status === 404) {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: 'Cupen du efterfrågade finns inte.',
                });
                navigate(`/upcoming`);
            }
            setError('An error occurred while fetching the class.');
            setCup({});
        } finally {
            setIsLoading(false);
        }
    }, [cupId, navigate]);  // Dependencies that `fetchCup` relies on

    useEffect(() => {
        fetchCup();
    }, [fetchCup]);  // Now it's stable and can be safely included

    const fetchParticipants = useCallback(async () => {
        try {
            setParticipantsIsLoading(true);
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/${cup?.competition?.id}/participants`);

            setParticipants(response.data);
            setError(null);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setError('Internt serverfel: Deltagare ej funna.');
            } else {
                setError('Ett fel inträffade vid inhämtning av deltagardata. Var god försök senare.');
            }
            setParticipants([]);
        } finally {
            setParticipantsIsLoading(false);
        }
    }, [cup?.competition?.id]);

    useEffect(() => {
        if (!isLoading) {
            fetchParticipants();
        }
    }, [fetchParticipants, isLoading]);

    const fetchParticipatingBunnies = useCallback(async () => {
        try {
            setBunniesLoading(true);
            const response = await api.get(
                `https://skhrf-api.cmdigital.se/v1/competitions/${cup?.competition?.id}/bunnies`
            );
            setParticipatingBunnies(response.data);

        } catch (error) {
            console.error('Error fetching participaing bunnies');
        } finally {
            setBunniesLoading(false);
        }
    }, [cup?.competition?.id]);

    useEffect(() => {
        if (!isLoading) {
            fetchParticipatingBunnies();
        }
    }, [fetchParticipatingBunnies, isLoading]);

    if (error) return <div className='flex w-full items-center justify-center'><div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'><h1 className='font-medium text-2xl'>Error</h1><p className='text-red-500'>{error}</p></div></div>;

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>

                <div className='grid grid-cols-1 lg:grid-cols-2 items-center'>
                    <div>
                        {isLoading ? (
                            <div className='flex flex-col gap-3'>
                                <Skeleton className='h-8 w-full' />
                                <Skeleton className='h-4 w-40' />
                            </div>
                        ) : (
                            <>
                                <h1 className='font-medium text-2xl'>Cupdetaljer för {cup?.name}
                                    {cup?.canceled && <Badge variant='destructive' className='ml-2'>Inställd</Badge>}
                                </h1>
                                <Breadcrumb className='mt-2'>
                                    <BreadcrumbList>
                                        <BreadcrumbItem>
                                            {cup?.competition?.finished_at ? <BreadcrumbLink asChild><Link to="/results">Resultat</Link></BreadcrumbLink> : <BreadcrumbLink asChild ><Link to="/upcoming">Kommande tävlingar</Link></BreadcrumbLink>}
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbLink asChild><Link to={`/competition/${cup?.competition?.id}`}>{cup?.competition?.name}</Link></BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbPage>{cup?.name}</BreadcrumbPage>
                                        </BreadcrumbItem>
                                    </BreadcrumbList>
                                </Breadcrumb>
                            </>
                        )}
                    </div>
                    {(!isLoading && (auth?.user?.superadmin || auth?.user?.judge || auth?.user?.roleInClub === 'admin')) &&
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 justify-end'>
                            <Dialog modal={true}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild className='md:col-start-2'>
                                        <Button variant='secondary'><DotsHorizontalIcon className='mr-3' />Verktyg</Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className='dropdown-content-width-full'>
                                        <DropdownMenuLabel>Verktyg</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        {((auth.user?.judge || auth.user?.superadmin) && cup?.results.length > 0) && <RegenerateCupResultsDialog cupId={cup?.id} fetchCup={fetchCup} />}
                                        <DeleteCupDialog cupId={cup?.id} fetchCup={fetchCup} />
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </Dialog>
                        </div>
                    }
                </div>

                {/* Display if results exist or if some of the classes are not finished */}
                {cup?.results && cup?.results?.length > 0 ? (
                    <>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 items-center'>
                            <div>
                                <h2 className='font-medium text-xl mt-4'>Cupresultat</h2>

                            </div>
                        </div>
                        <CupResultsDatatable cupResults={cup?.results} isLoading={isLoading} />
                        {isLoading ? (
                            <Skeleton className='h-4 w-1/3' />
                        ) : (
                            <p className='text-muted-foreground text-sm'>Resultat beräknat vid {new Date(cup?.genereted_results_at).toLocaleString("sv-SE")}</p>
                        )}
                    </>
                ) : null}

                <div className='grid grid-cols-1 md:grid-cols-2 gap-3 items-center'>
                    <div>
                        <h2 className='font-medium text-xl mt-4'>Tillhörande klasser</h2>
                    </div>
                </div>
                {!isLoading ? (
                    <CompetitionSportClassesDatatable isLoading={isLoading} sportClasses={cup?.classes} fetchCompetition={fetchCup} competition={cup?.competition} singleCup={cup} />
                ) : (
                    <Skeleton className='w-full h-8' />
                )
                }

                {!participantsIsLoading && !isLoading && !bunniesLoading && (<>
                    <h2 className='font-medium text-xl mt-5'>Deltagare</h2>

                    <CompetitionClassRegistrationsCollapsibleDatatable competitionClass={cup} isLoading={participantsIsLoading} fetchClass={fetchCup} forCup={true} participants={participants} bunnies={participatingBunnies} />
                </>
                )}

                {cup?.results && cup?.results.length && (auth.user?.superadmin || auth.user?.judge) > 0 ? (
                    <Accordion type="single" collapsible>
                        <AccordionItem value="item-1">
                            <AccordionTrigger>Beräkningsförklaring</AccordionTrigger>
                            <AccordionContent className='flex flex-col gap-6'>
                                <p className='text-muted-foreground text-sm'>Observera att beräkningsförklaringar är enbart synliga för domare och superadministratörer.</p>
                                <div>
                                    {isLoading ? (
                                        <Skeleton className='h-16 w-full' />
                                    ) : (
                                        <pre>{JSON.stringify(JSON.parse(cup?.explanation), null, 2)}</pre>
                                    )}
                                </div>

                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                ) : null}

            </div>
        </div>
    );
};

export default CupSingle;
